import { omit } from "lodash";
import { SingleSelectOption } from "@/components/controls/SingleSelect";
import {
  HubSpotForm,
  HubSpotFormConfig,
  HubSpotFormsSettings,
  HubSpotFormsSettingsUpdate,
  OwnerRule
} from "@/entities/hubspot/types";
import { FormProductMap } from "@/entities/formProductMap/types";
import { UserData, UserStatus } from "@/entities/users/types";
import { LeadType } from "@/pages/leads/types";

export const HUBSPOT_SETTINGS_TRANSLATION_PREFIX =
  "integrations.hubspotSettings";

// The backend's own limits, so a save it would refuse is never offered.
export const MAX_SELECTED_FORMS = 50;
export const MAX_OWNER_RULES = 20;
export const MAX_NOTE_PROPERTIES = 20;
export const MAX_VALUE_LABELS = 100;
export const MAX_NAME_LENGTH = 255;

export const LEAD_SOURCES = [LeadType.INBOUND, LeadType.OUTBOUND];

// Server-set fields are dropped; everything else goes back as it was read, so
// a save never loses a field this page doesn't edit.
export const toFormUpdate = (form: HubSpotFormConfig): HubSpotFormConfig =>
  omit(form, "selectedAt");

export const toSettingsUpdate = (
  settings: HubSpotFormsSettings
): HubSpotFormsSettingsUpdate => ({
  ...omit(settings, "enabledAt"),
  forms: settings.forms.map(toFormUpdate)
});

export const newFormConfig = (form: HubSpotForm): HubSpotFormConfig => ({
  formId: form.id,
  formName: form.name,
  source: LeadType.INBOUND,
  inboundCampaignType: null,
  productInterest: {
    origin: "none",
    property: null,
    value: null,
    valueLabels: {}
  },
  ownerRules: [],
  noteProperties: []
});

export interface FormRow {
  id: string;
  name: string;
  config?: HubSpotFormConfig;
  // Selected, but HubSpot no longer lists it.
  missing: boolean;
}

// HubSpot's forms in its order, then any selected form HubSpot no longer lists.
export const formRows = (
  forms: HubSpotForm[],
  selected: HubSpotFormConfig[],
  formsLoaded: boolean
): FormRow[] => {
  const byId = new Map(selected.map((config) => [config.formId, config]));
  const listedIds = new Set(forms.map((form) => form.id));
  return [
    ...forms.map((form) => ({
      id: form.id,
      name: form.name,
      config: byId.get(form.id),
      missing: false
    })),
    ...selected
      .filter((config) => !listedIds.has(config.formId))
      .map((config) => ({
        id: config.formId,
        name: config.formName,
        config,
        missing: formsLoaded
      }))
  ];
};

const sameEmail = (left: string, right: string) =>
  left.trim().toLowerCase() === right.trim().toLowerCase();

const activeUsersWithEmail = (users: UserData[]) =>
  users.filter(
    (user): user is UserData & { email: string } =>
      Boolean(user.email) &&
      (user.status === undefined || user.status === UserStatus.ACTIVE)
  );

// Owners are stored by email, so the options are keyed by it. The saved owner
// stays selectable when the search didn't return them: named from the users
// found by searching their email, or marked unknown once that search found no
// active user with it. `savedOwnerMatches` is undefined while it is running.
export const ownerOptions = (
  users: UserData[],
  savedEmail: string | null,
  savedOwnerMatches: UserData[] | undefined,
  unknownUserLabel: string
): SingleSelectOption[] => {
  const options: SingleSelectOption[] = activeUsersWithEmail(users).map(
    (user) => ({
      id: user.email,
      displayName: user.displayName || user.email,
      subtitle: user.email
    })
  );
  if (
    !savedEmail ||
    options.some((option) => sameEmail(String(option.id), savedEmail))
  ) {
    return options;
  }
  const savedUser =
    savedOwnerMatches &&
    activeUsersWithEmail(savedOwnerMatches).find((user) =>
      sameEmail(user.email, savedEmail)
    );
  return [
    ...options,
    {
      id: savedEmail,
      displayName: savedUser?.displayName || savedEmail,
      subtitle: savedUser
        ? savedUser.email
        : savedOwnerMatches
          ? unknownUserLabel
          : ""
    }
  ];
};

// The saved email exactly as stored, when only its case differs from a user's.
export const matchingOptionEmail = (
  options: SingleSelectOption[],
  email: string | null
): string | null => {
  if (!email) return null;
  const match = options.find((option) => sameEmail(String(option.id), email));
  return match ? String(match.id) : email;
};

export const parseProducts = (text: string): string[] =>
  text
    .split(",")
    .map((product) => product.trim())
    .filter(Boolean);

export const formatProducts = (products: string[]): string =>
  products.join(", ");

const normalize = (value: string) => value.split(/\s+/).join(" ").toLowerCase();

export type FormConfigError =
  | "propertyRequired"
  | "valueRequired"
  | "ownerRequired"
  | "fallbackNotLast"
  | "repeatedValueLabel"
  | "incompleteValueLabel"
  | "incompleteNoteProperty";

const isFallback = (rule: OwnerRule) => rule.products.length === 0;

// The checks the backend applies to one form, reported before a save is sent.
export const formConfigErrors = (
  config: HubSpotFormConfig,
  valueLabelRows: [string, string][]
): FormConfigError[] => {
  const errors: FormConfigError[] = [];
  const { origin, property, value } = config.productInterest;
  if (origin === "field" && !property?.trim()) errors.push("propertyRequired");
  if (origin === "fixed" && !value?.trim()) errors.push("valueRequired");
  if (
    config.ownerRules.some(
      (rule) => !rule.owners.sdrOwner && !rule.owners.aeOwner
    )
  ) {
    errors.push("ownerRequired");
  }
  const fallbackIndex = config.ownerRules.findIndex(isFallback);
  if (fallbackIndex !== -1 && fallbackIndex !== config.ownerRules.length - 1) {
    errors.push("fallbackNotLast");
  }
  if (origin === "field") {
    const filled = valueLabelRows.filter(
      ([key, label]) => key.trim() || label.trim()
    );
    if (filled.some(([key, label]) => !key.trim() || !label.trim())) {
      errors.push("incompleteValueLabel");
    }
    const keys = filled.map(([key]) => normalize(key.trim()));
    if (new Set(keys).size !== keys.length) errors.push("repeatedValueLabel");
  }
  if (
    config.noteProperties.some(
      (note) => !note.property.trim() || !note.label.trim()
    )
  ) {
    errors.push("incompleteNoteProperty");
  }
  return errors;
};

export const valueLabelsFromRows = (
  rows: [string, string][]
): Record<string, string> =>
  Object.fromEntries(
    rows
      .filter(([key, label]) => key.trim() && label.trim())
      .map(([key, label]) => [key.trim(), label.trim()])
  );

export const blankToNull = (value: string | null): string | null =>
  value?.trim() ? value.trim() : null;

export const MAX_PRODUCT_RULES = 100;
export const MAX_RULE_VALUES = 100;

// A product map rule as edited: its values typed as text.
export interface ProductRuleRow {
  valuesText: string;
  productId: string | null;
}

export const productRuleRows = (map: FormProductMap): ProductRuleRow[] =>
  map.rules.map((rule) => ({
    valuesText: formatProducts(rule.values),
    productId: rule.productId
  }));

export type ProductMapError =
  | "valuesRequired"
  | "productRequired"
  | "productNotInCatalog"
  | "repeatedValue"
  | "tooManyValues"
  | "valueTooLong";

// The checks the backend applies to the map, reported before a save is sent.
export const productMapErrors = (
  rows: ProductRuleRow[],
  catalogIds: Set<string>
): ProductMapError[] => {
  const errors: ProductMapError[] = [];
  const values = rows.map((row) => parseProducts(row.valuesText));
  if (values.some((ruleValues) => ruleValues.length === 0)) {
    errors.push("valuesRequired");
  }
  if (rows.some((row) => !row.productId)) errors.push("productRequired");
  if (rows.some((row) => row.productId && !catalogIds.has(row.productId))) {
    errors.push("productNotInCatalog");
  }
  const normalized = values.flat().map(normalize);
  if (new Set(normalized).size !== normalized.length) {
    errors.push("repeatedValue");
  }
  if (values.some((ruleValues) => ruleValues.length > MAX_RULE_VALUES)) {
    errors.push("tooManyValues");
  }
  if (values.flat().some((value) => value.length > MAX_NAME_LENGTH)) {
    errors.push("valueTooLong");
  }
  return errors;
};

export const productMapFromRows = (rows: ProductRuleRow[]): FormProductMap => ({
  rules: rows.map((row) => ({
    values: parseProducts(row.valuesText),
    productId: row.productId ?? ""
  }))
});
